import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query as firestoreQuery, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiArchive, FiDownload, FiEdit2, FiPlus, FiSearch, FiTrash2, FiUserCheck } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { unitsOf, useSessionUser } from '../../lib/permissions';
import { unitConstraints } from '../../lib/unitQuery';
import { formatDate } from '../../lib/format';
import { archiveDocuments, documentError, sendFiles } from '../../lib/documents';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Field, INPUT, Modal, Notice, PageHeader, Pill, Segmented, SIZE, SkeletonRows, TextArea } from '../ui/kit';
import { initials, statusLabel, statusTone } from '../patients/patientModel';
import PatientPicker from '../patients/PatientPicker';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import FormerRecords from './FormerRecords';
import { archiveAccess, countFiles, documentsOf, exportArchives, isArchivable, legacyLinks, patientIdOf, patientOf, searchText, snapshotOf } from './archiveModel';

const PAGE_SIZE = 30;
const ICON_BUTTON =
  'flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-black/40 transition hover:bg-black/[0.05] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40';

const emptyForm = () => ({ patientId: '', comment: '', documents: [], removed: [], links: [], pending: [] });

const ArchiveRow = memo(({ row, editable, deletable, onEdit, onDelete, onOpen }) => {
  const { entry, patient } = row;
  const links = legacyLinks(entry);
  const name = patient.nom || 'Patient non précisé';
  return (
    <li className="flex items-start gap-[14px] border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[14px] last:border-b-0 sm:px-6">
      <span className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-full bg-slate-100 text-[13px] font-semibold text-slate-600" aria-hidden="true">
        {initials(name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-1">
          {row.known ? (
            <Link to={`/patients/${patientIdOf(entry)}`} className="truncate text-[15px] font-semibold text-ink no-underline hover:text-brand-600">
              {name}
            </Link>
          ) : (
            <span className="truncate text-[15px] font-semibold text-ink">{name}</span>
          )}
          {patient.statut && <Pill tone={statusTone(patient.statut)}>{statusLabel(patient.statut)}</Pill>}
        </div>
        <p className="mb-0 mt-0.5 truncate text-[13px] text-black/45">
          {[`Archivé le ${row.dateLabel || 'date inconnue'}`, patient.dossierNumber && `Dossier ${patient.dossierNumber}`, entry.service].filter(Boolean).join(' · ')}
        </p>
        {entry.comment && <p className="mb-0 mt-1.5 whitespace-pre-wrap text-[14px] leading-relaxed text-black/65">{entry.comment}</p>}
        {(row.documents.length > 0 || links.length > 0) && (
          <div className="mt-2">
            <DocumentLinks documents={row.documents} links={links} onOpen={(document) => onOpen(document, entry)} size="sm" />
          </div>
        )}
      </div>
      {(editable || deletable) && (
        <div className="flex flex-none items-center">
          {editable && (
            <button type="button" onClick={() => onEdit(entry)} className={ICON_BUTTON} title="Modifier" aria-label={`Modifier l’archive de ${name}`}>
              <FiEdit2 size={16} aria-hidden="true" />
            </button>
          )}
          {deletable && (
            <button type="button" onClick={() => onDelete(entry)} className={`${ICON_BUTTON} hover:text-red-600`} title="Retirer" aria-label={`Retirer l’archive de ${name}`}>
              <FiTrash2 size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </li>
  );
});

const PendingRow = memo(({ patient, canAdd, onArchive }) => (
  <li className="flex items-center gap-[14px] border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[14px] last:border-b-0 sm:px-6">
    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-black/[0.05] text-[13px] font-semibold text-black/60" aria-hidden="true">
      {initials(patient.nom || '?')}
    </span>
    <span className="min-w-0 flex-1">
      <Link to={`/patients/${patient._id}`} className="block truncate text-[15px] font-semibold text-ink no-underline hover:text-brand-600">
        {patient.nom || 'Nom non précisé'}
      </Link>
      <span className="mt-0.5 block truncate text-[13px] text-black/45">
        {[patient.dossierNumber && `Dossier ${patient.dossierNumber}`, patient.services, patient.dateSortie && `sorti le ${formatDate(patient.dateSortie) || patient.dateSortie}`].filter(Boolean).join(' · ')}
      </span>
    </span>
    <Pill tone={statusTone(patient.statut)}>{statusLabel(patient.statut)}</Pill>
    {canAdd && (
      <button type="button" onClick={() => onArchive(patient)} className={`${BUTTON.secondary} ${SIZE.sm} flex-none`} aria-label={`Archiver le dossier de ${patient.nom}`}>
        <FiArchive size={14} aria-hidden="true" />
        <span className="hidden sm:inline">Archiver</span>
      </button>
    )}
  </li>
));

const ArchivesPage = () => {
  const user = useSessionUser();
  const access = archiveAccess(user);
  const documentsStatus = useDocumentsStatus();
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('loading');
  const [view, setView] = useState('archives');
  const [search, setSearch] = useState('');
  const [unit, setUnit] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dialog, setDialog] = useState(null); // { mode: 'form', item? } | { mode: 'delete', item }
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  const [viewing, setViewing] = useState(null); // { document, item }

  // Only the archives of the account's units (Firestore rules).
  const constraints = useMemo(() => unitConstraints(user), [user]);
  useEffect(() => {
    ensurePatients().catch(() => {});
    if (!constraints) {
      setStatus('ready');
      return undefined;
    }
    return onSnapshot(
      firestoreQuery(collection(db, 'archives'), ...constraints),
      (snapshot) => {
        setEntries(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setStatus('ready');
      },
      () => setStatus('error')
    );
  }, [ensurePatients, constraints]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const rows = useMemo(
    () =>
      entries
        .filter((entry) => access.visible(entry))
        .map((entry) => {
          const patient = patientOf(entry, patientsById);
          return {
            entry,
            patient,
            known: Boolean(patientsById[patientIdOf(entry)]),
            documents: documentsOf(entry),
            time: new Date(entry.timestamp || 0).getTime() || 0,
            dateLabel: formatDate(entry.timestamp),
            search: searchText(entry, patient),
          };
        })
        .sort((a, b) => b.time - a.time),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, patientsById, user.role, user.service]
  );
  const archivedIds = useMemo(() => new Set(entries.map(patientIdOf).filter(Boolean)), [entries]);
  // Deceased and discharged patients of the account's units that have no archive yet.
  const pending = useMemo(
    () =>
      patients
        .filter((patient) => isArchivable(patient) && access.ownsPatient(patient) && !archivedIds.has(patient._id))
        .sort((a, b) => String(b.dateSortie || b.updatedAt || '').localeCompare(String(a.dateSortie || a.updatedAt || ''))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patients, archivedIds, user.role, user.service]
  );
  const unitsInData = useMemo(() => [...new Set(rows.map((row) => row.entry.service).filter(Boolean))].sort(), [rows]);

  const query = useDeferredValue(search.trim().toLowerCase());
  const words = query ? query.split(/\s+/) : [];
  const shown = useMemo(
    () => rows.filter((row) => (!unit || row.entry.service === unit) && words.every((word) => row.search.includes(word))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, unit, query]
  );
  const shownPending = useMemo(
    () => (words.length ? pending.filter((patient) => words.every((word) => [patient.nom, patient.dossierNumber, patient.services].join(' ').toLowerCase().includes(word))) : pending),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pending, query]
  );
  useEffect(() => setLimit(PAGE_SIZE), [unit, query, view]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const openAdd = useCallback((patient) => {
    setForm({ ...emptyForm(), patientId: patient ? patient._id : '' });
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form' });
  }, []);
  const openEdit = useCallback((item) => {
    setForm({ ...emptyForm(), patientId: patientIdOf(item), comment: item.comment || '', documents: documentsOf(item), links: legacyLinks(item) });
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form', item });
  }, []);
  const openDelete = useCallback((item) => {
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'delete', item });
  }, []);
  const openDocument = useCallback((document, item) => setViewing({ document, item }), []);
  const closeDialog = () => !busy && setDialog(null);

  const editingItem = dialog && dialog.mode === 'form' ? dialog.item : null;
  // In the form: deceased or discharged patients of the account's units, not archived yet.
  const pickable = useCallback(
    (patient) => isArchivable(patient) && access.ownsPatient(patient) && (!archivedIds.has(patient._id) || (editingItem && patientIdOf(editingItem) === patient._id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [archivedIds, editingItem, user.role, user.service]
  );

  const save = async () => {
    const patient = patientsById[form.patientId] || (editingItem && patientIdOf(editingItem) === form.patientId ? editingItem.patient : null);
    if (!patient) {
      setDialogError('Choisissez le dossier du patient à archiver.');
      return;
    }
    setBusy(true);
    setDialogError('');
    const entryUnit = (editingItem && editingItem.service) || patient.services || user.service || '';
    let documents = form.documents;
    try {
      if (form.pending.length) {
        try {
          documents = [...documents, ...(await sendFiles(form.pending, { section: 'archives', patientId: form.patientId, unit: entryUnit }, setSending))];
          setForm((current) => ({ ...current, documents, pending: [] }));
        } catch (sendError) {
          const sent = sendError.sent || [];
          documents = [...documents, ...sent];
          setForm((current) => ({ ...current, documents, pending: current.pending.slice(sent.length) }));
          throw sendError;
        } finally {
          setSending(null);
        }
      }
      if (form.removed.length) {
        const failed = await archiveDocuments(form.removed);
        setForm((current) => ({ ...current, removed: [] }));
        if (failed.length) {
          documents = [...documents, ...failed];
          setForm((current) => ({ ...current, documents }));
          throw new Error('archive');
        }
      }
      const record = {
        patientId: form.patientId,
        patient: patientsById[form.patientId] ? snapshotOf(patientsById[form.patientId]) : patient,
        comment: form.comment.trim(),
        service: entryUnit,
        units: unitsOf(entryUnit),
        documents: documents.map(({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' })),
        dropboxLinks: form.links.map(({ link, name }) => ({ link, name })),
      };
      const now = new Date().toISOString();
      if (editingItem) {
        await updateDoc(doc(db, 'archives', editingItem.id), { ...record, updatedBy: user.name || '', updatedAt: now });
        setNotice('Archive mise à jour.');
      } else {
        await addDoc(collection(db, 'archives'), { ...record, timestamp: now, createdBy: user.name || '', createdAt: now });
        setNotice(`Dossier de ${record.patient.nom || 'ce patient'} archivé.`);
        setView('archives');
      }
      setDialog(null);
    } catch (error) {
      setDialogError(
        error.message === 'archive'
          ? 'Un document n’a pas pu être retiré. Réessayez.'
          : await documentError(error, 'L’archive n’a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.')
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const failed = await archiveDocuments(documentsOf(dialog.item));
      if (failed.length) {
        await updateDoc(doc(db, 'archives', dialog.item.id), { documents: failed });
        throw new Error('archive');
      }
      await deleteDoc(doc(db, 'archives', dialog.item.id));
      setDialog(null);
      setNotice('Archive retirée. Ses documents restent conservés dans les archives du Dropbox du service.');
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Un document n’a pas pu être retiré. Réessayez.' : 'L’archive n’a pas pu être retirée. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  const list = view === 'archives' ? shown : shownPending;
  const plural = (count, one, many) => `${count} ${count > 1 ? many : one}`;

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Espace protégé"
        title="Archives"
        subtitle={
          status === 'ready'
            ? `${plural(rows.length, 'dossier archivé', 'dossiers archivés')} · ${access.isAdmin ? 'toutes les unités' : `unité ${user.service || 'non précisée'}`}`
            : 'Chargement des archives…'
        }
        actions={
          <>
            {access.canExport && view === 'archives' && (
              <button type="button" onClick={() => exportArchives(shown)} disabled={!shown.length} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiDownload size={15} aria-hidden="true" />
                Excel
              </button>
            )}
            {access.canAdd && (
              <button type="button" onClick={() => openAdd(null)} className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiPlus size={16} aria-hidden="true" />
                Archiver un dossier
              </button>
            )}
          </>
        }
      />

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Segmented
            value={view}
            onChange={setView}
            allowEmpty={false}
            options={[
              { value: 'archives', label: `Archivés (${rows.length})` },
              { value: 'pending', label: `À archiver (${pending.length})` },
              { value: 'former', label: 'Anciens dossiers' },
            ]}
          />
        </div>
        {notice && <Notice tone="green">{notice}</Notice>}
        {!access.canAdd && view !== 'former' && (
          <p className="m-0 text-[13.5px] text-black/45">Vous consultez les archives ; l’archivage est fait par les archivistes.</p>
        )}
      </div>

      {view === 'former' ? (
        <FormerRecords />
      ) : (
        <>
          <div className="mt-[16px] flex flex-col gap-[12px] lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={view === 'archives' ? 'Patient, n° de dossier, commentaire, document…' : 'Patient, n° de dossier…'}
                aria-label="Rechercher dans les archives"
                className={`${INPUT} h-11 pl-[42px]`}
              />
            </div>
            {view === 'archives' && unitsInData.length > 1 && (
              <Segmented value={unit} onChange={setUnit} allowEmpty={false} options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]} />
            )}
          </div>

          <section className={`${CARD} mt-[16px] overflow-hidden`}>
            {status === 'loading' ? (
              <div className="p-6">
                <SkeletonRows rows={6} />
              </div>
            ) : status === 'error' ? (
              <EmptyState icon={FiAlertCircle} title="Archives indisponibles">
                Elles n’ont pas pu être chargées. Vérifiez la connexion, puis rechargez la page.
              </EmptyState>
            ) : list.length === 0 ? (
              view === 'archives' ? (
                <EmptyState icon={FiArchive} title="Aucune archive">
                  {rows.length ? 'Aucune archive ne correspond à cette recherche.' : 'Les dossiers archivés de votre unité apparaîtront ici.'}
                </EmptyState>
              ) : (
                <EmptyState icon={FiUserCheck} title="Rien à archiver">
                  {pending.length ? 'Aucun dossier ne correspond à cette recherche.' : 'Tous les dossiers décédés et exeat de votre unité sont archivés.'}
                </EmptyState>
              )
            ) : (
              <>
                <ul className="m-0 list-none p-0">
                  {view === 'archives'
                    ? list.slice(0, limit).map((row) => (
                        <ArchiveRow
                          key={row.entry.id}
                          row={row}
                          editable={access.canEdit(row.entry)}
                          deletable={access.canDelete(row.entry)}
                          onEdit={openEdit}
                          onDelete={openDelete}
                          onOpen={openDocument}
                        />
                      ))
                    : list.slice(0, limit).map((patient) => <PendingRow key={patient._id} patient={patient} canAdd={access.canAdd} onArchive={openAdd} />)}
                </ul>
                {limit < list.length && (
                  <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                    <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                      Afficher {Math.min(PAGE_SIZE, list.length - limit)} dossiers de plus
                    </button>
                    <span className="text-[12.5px] text-black/40">
                      {limit} sur {list.length} affichés
                    </span>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      <Modal
        open={Boolean(dialog && dialog.mode === 'form')}
        title={editingItem ? 'Modifier l’archive' : 'Archiver un dossier'}
        onClose={closeDialog}
        size="lg"
        footer={
          <>
            <button type="button" onClick={closeDialog} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={save} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {sending ? `Envoi ${sending.index + 1}/${form.pending.length}…` : busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          <Field id="archive-patient" label="Dossier patient" required hint="Patients décédés ou exeat de votre unité, pas encore archivés.">
            <PatientPicker
              id="archive-patient"
              single
              filter={pickable}
              values={form.patientId ? [form.patientId] : []}
              onChange={(ids) => {
                set('patientId', ids[0] || '');
                setDialogError('');
              }}
            />
          </Field>
          <Field id="archive-comment" label="Commentaire">
            <TextArea id="archive-comment" value={form.comment} onChange={(e) => set('comment', e.target.value)} placeholder="Circonstances, pièces manquantes, emplacement du dossier papier…" />
          </Field>
          <Field id="archive-documents" label="Documents">
            <AttachmentsField
              id="archive-documents"
              status={documentsStatus}
              stored={form.documents}
              onOpen={(document) => setViewing({ document, item: editingItem })}
              onRemoveStored={busy ? null : (item) => setForm((current) => ({ ...current, documents: current.documents.filter((entry) => entry.id !== item.id), removed: [...current.removed, item] }))}
              links={form.links}
              onLinksChange={busy ? null : (links) => set('links', links)}
              pending={form.pending}
              onPendingChange={(files) => {
                set('pending', files);
                setDialogError('');
              }}
              sending={sending}
            />
          </Field>
          {form.removed.length > 0 && (
            <p className="m-0 text-[12.5px] text-black/45">
              {form.removed.length === 1 ? 'Un document sera retiré' : `${form.removed.length} documents seront retirés`} à l’enregistrement (conservés dans les archives du
              Dropbox du service).
            </p>
          )}
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title="Retirer l’archive"
        onClose={closeDialog}
        footer={
          <>
            <button type="button" onClick={closeDialog} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Retrait…' : 'Retirer'}
            </button>
          </>
        }
      >
        {dialog && dialog.mode === 'delete' && (
          <div className="flex flex-col gap-[12px]">
            <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
              L’archive du dossier de <strong className="text-ink">{patientOf(dialog.item, patientsById).nom || 'ce patient'}</strong> sera retirée de la liste. Le dossier patient
              n’est pas modifié{countFiles(dialog.item) ? ' ; ses documents restent conservés dans les archives du Dropbox du service' : ''}.
            </p>
            {dialogError && <Notice tone="red">{dialogError}</Notice>}
          </div>
        )}
      </Modal>

      {viewing && (
        <DocumentViewer
          item={viewing.document}
          onClose={() => setViewing(null)}
          canReplace={Boolean(viewing.item) && access.canEdit(viewing.item)}
          canDownload={access.canExport}
        />
      )}
    </main>
  );
};

export default ArchivesPage;
